import React, { useEffect } from 'react';
// import { Controller } from 'react-hook-form';
import {
  ATMHeader,
  ATMSegment,
  ATMGrid,
  ATMField,
  ATMForm,
  ATMCheckbox,
} from 'shared-it-appmod-ui';
import { useFireFightingDispatchContext } from 'src/contexts/fire-fighting-dispatch.context';
import { INewFireDispatchMethods } from 'src/models/fire-fighting-dispatch.model';
import IconText from 'src/components/atoms/icon-text/icon-text.component';
import Lang from 'src/libraries/language';
import { useSettings } from 'src/hooks/setting.hook';
import { SettingKey } from 'src/constants';
import FlightInformationAirDropsTable from './air-drops-table/air-drops-table.component';
import style from './flight-information-form.module.scss';

type IProps = INewFireDispatchMethods & {
  isEdit?: boolean;
};

const getDataNumbers = (max, key) => {
  return Array.from({ length: max }, (_, i) => i + 1).map((n: number) => {
    return {
      key: `${key}${n}`,
      value: n,
      text: n,
    };
  });
};

interface IFNProps extends INewFireDispatchMethods {
  flightNumber: string | null | undefined;
}

const FlightNumber: React.FC<IFNProps> = ({ control, flightNumber }) => {
  if (flightNumber) {
    return (
      <ATMField
        readOnly
        label={Lang.LBL_FIRE_DISPATCH_FLIGHT_NUMBER}
        name="flightNumber"
        width={12}
        as={ATMForm.Input}
        control={control}
      />
    );
  }
  return (
    <>
      <label>{Lang.LBL_FIRE_DISPATCH_FLIGHT_NUMBER}</label>
      <IconText
        text={Lang.MSG_FIRE_DISPATCH_FLIGHT_NUMBER}
        icon="warning circle"
        size="small"
      />
    </>
  );
};

const FlightInformationForm: React.FC<IProps> = ({
  control,
  errors,
  isEdit = false,
}) => {
  const { state, actions } = useFireFightingDispatchContext();
  const { FuelCycles = 10, LandingCycles = 10 } = useSettings([
    SettingKey.FuelCycles,
    SettingKey.LandingCycles,
  ]);
  const operatorID = control.watchInternal('operatorID');

  const onOperatorIdChanged = () => {
    control.setValue('tailNumber', null);
    control.setValue('pilot', null);
    control.setValue('coPilot', null);
  };

  useEffect(() => {
    if (operatorID) {
      actions.getTailAndPilotsByHelicopterCompany(Number(operatorID) || 0);
      const operatorsName = state.helicopterCompanyList.filter((opts) => {
        return opts.operatorId === operatorID;
      });
      if (operatorsName.length) {
        control.setValue('operatorName', operatorsName[0].name);
      }
    } else {
      onOperatorIdChanged();
    }
  }, [operatorID]);

  if (!isEdit) {
    state.defaultValues.operatorID = null;
    state.defaultValues.fuelCycles = null;
    state.defaultValues.landingCycles = null;
  }
  if (state.defaultValues.fuelCycles === 0) {
    state.defaultValues.fuelCycles = null;
  }
  if (state.defaultValues.landingCycles === 0) {
    state.defaultValues.landingCycles = null;
  }
  return (
    <ATMSegment>
      <ATMHeader>{Lang.TTL_FIRE_DISPATCH_FLIGHT_INFORMATION}</ATMHeader>
      <ATMGrid columns={4}>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <ATMField
              name="operatorName"
              className="hidden"
              as={ATMForm.Input}
              control={control}
              type="hidden"
            />
            <ATMField
              name="operatorID"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_HELICOPTER_COMPANY}
              as={ATMForm.Select}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={state.helicopterCompanyList.map((value) => ({
                key: value.operatorId,
                value: value.operatorId,
                text: value.name,
              }))}
              onChange={([_, { value }]) => {
                onOperatorIdChanged();
                return value;
              }}
              error={errors.helicopterCompany}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="tailNumber"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_TAIL}
              as={ATMForm.Select}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={state.tailNumberList.map((value) => ({
                key: value.tailId,
                value: value.tailNumber,
                text: value.tailNumber,
              }))}
              onChange={([_, { value }]) => value}
              error={errors.tailNumber}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="pilot"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_PILOT}
              as={ATMForm.Select}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={state.pilotList.map((value) => ({
                key: value.pilotId,
                value: value.name,
                text: value.name,
              }))}
              onChange={([_, { value }]) => value}
              error={errors.pilot}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="coPilot"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_COPILOT}
              as={ATMForm.Select}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={state.pilotList.map((value) => ({
                key: value.pilotId,
                value: value.name,
                text: value.name,
              }))}
              onChange={([_, { value }]) => value}
              error={errors.coPilot}
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row>
          <ATMGrid.Column>
            <FlightNumber
              control={control}
              errors={errors}
              flightNumber={state.defaultValues.flightNumber}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="fuelCycles"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_FUEL_CYCLES}
              as={ATMForm.Select}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={getDataNumbers(FuelCycles, 'fc')}
              onChange={([_, { value }]) => value}
              error={errors.fuelCycles}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMField
              name="landingCycles"
              width={12}
              label={Lang.LBL_FIRE_DISPATCH_LANDING_CYCLES}
              as={ATMForm.Select}
              placeholder={Lang.LBL_SELECT}
              control={control}
              options={getDataNumbers(LandingCycles, 'lc')}
              onChange={([_, { value }]) => value}
              error={errors.landingCycles}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <span className={style.checkbox}>
              <ATMField
                name="billableLaunch"
                as={ATMCheckbox}
                control={control}
                error={errors.billableLaunch}
                defaultChecked={state.defaultValues.billableLaunch === 'true'}
                onChange={([_, value]) => value.checked}
                style={{ display: 'inline' }}
              />
              <span>{Lang.LBL_FIRE_DISPATCH_BILLABLE_LAUNCH}</span>
            </span>
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>
      <FlightInformationAirDropsTable control={control} errors={errors} />
    </ATMSegment>
  );
};

export default FlightInformationForm;
